import React, { useEffect, useMemo, useContext } from 'react';
import EditorBaseTheme from '../DaEditor/components/BaseTheme';
import isEqual from 'react-fast-compare';
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import Notifications from '../Notifications';
import LiveNotifications from '../LiveNotifications';
import ErrorBoundary from '../ErrorBoundary';
import Modals from '../Modals';
import BackToTop from '../BackToTop';
import Head from './Head';
import { SisuContext } from '../Context';
import { SessionContext, } from '@wix/da-react-context/pkg/SessionContext';
import { ThemeContext, ThemeSkinContext, } from '@wix/da-react-context/pkg/ThemeContext';
import { MatureContext } from '@wix/da-react-context/pkg/MatureContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { HeadingLevelContext } from '@wix/da-a11y/pkg/components/headings/context';
import { UniqueIdContext, makeUniqueIdSource, } from '@wix/da-hooks/pkg/useUniqueId';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import FlagSwitcher from '../redux/flags/FlagSwitcher';
import { FirstTimeFlag } from '../redux/firstTimeFlags';
import VerificationPopup from '../VerificationPopup';
import DuperbrowsePageWrapper from '../Duperbrowse/DuperbrowsePageWrapper';
import { BrowserFeatureContext } from '@wix/da-react-context/pkg/BrowserFeatureContext';
import { NCContext } from '@wix/da-react-context/pkg/NCContext';
import { usePageVisibility } from '@wix/da-hooks/pkg/usePageVisibility';
import { ScreenSizeContextProvider } from '../Measured';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import CookiesBanner from '../Popup/CookiesBanner';
import ChatInit from '../Chat/ChatInit';
import DRE from '../DRE';
import DaAppUrl from './DaAppUrl';
import HoistCss from './HoistCss';
import TimeTravel from '../TimeTravel';
/**
 * This is designed to wrap your render() output in App.tsx
 * it provides the common components that we need for some
 * shared things.
 *
 * Things it includes:
 *
 * - <Notifications /> (for general system notifications when deleting, adding to groups, faving, etc)
 * - <LiveNotifications /> (for real time notifications pulled from hub)
 * - <Modals />
 *
 * The dream
 * This component wraps all the header/context etc but currently
 * there is some variation between projects, so for now its limited
 * in its scope.
 */
var DaApp = function (_a) {
    var appMounted = _a.appMounted, appUnmounted = _a.appUnmounted, appVisibilityChange = _a.appVisibilityChange, modalsRenderFunc = _a.modalsRenderFunc, _b = _a.showMatureContent, showMatureContent = _b === void 0 ? true : _b, _c = _a.isMobile, isMobile = _c === void 0 ? false : _c, theme = _a.theme, themeSkin = _a.themeSkin, language = _a.language, environment = _a.environment, bodyClassName = _a.bodyClassName, backToTopOffset = _a.backToTopOffset, _d = _a.isDuperbrowseEnabled, isDuperbrowseEnabled = _d === void 0 ? true : _d, isDuperbrowseActive = _a.isDuperbrowseActive, isLegacyHeader = _a.isLegacyHeader, _e = _a.supportsWebp, supportsWebp = _e === void 0 ? false : _e, _f = _a.supportsLazy, supportsLazy = _f === void 0 ? false : _f, publicSession = _a.publicSession, biPageContextData = _a.biPageContextData, disableHeadTags = _a.disableHeadTags, isLoggedIn = _a.isLoggedIn, firstTimeFlags = _a.firstTimeFlags, showCookieBanner = _a.showCookieBanner, children = _a.children, notificationsRenderFunc = _a.notificationsRenderFunc, notificationsWrapperClassName = _a.notificationsWrapperClassName, disablePageWideNotifications = _a.disablePageWideNotifications, disableChat = _a.disableChat, disableFloatingChat = _a.disableFloatingChat, flags = _a.flags, duperbrowseViewType = _a.duperbrowseViewType, isIframe = _a.isIframe, appUrl = _a.appUrl, _g = _a.ncContextProps, ncContextProps = _g === void 0 ? {} : _g, _h = _a.allowAppBanner, allowAppBanner = _h === void 0 ? true : _h;
    var shouldHideBackToTop = useFlag('bye_back_to_top');
    useEffect(function () {
        appMounted();
        return appUnmounted;
    }, [appMounted, appUnmounted]);
    usePageVisibility(appVisibilityChange);
    var browserSupport = useMemo(function () { return ({ supportsWebp: supportsWebp, supportsLazy: supportsLazy }); }, [supportsLazy, supportsWebp]);
    var uniqueIdSource = useMemo(function () { return makeUniqueIdSource('app-root'); }, []);
    var ncContextPropsMemo = useMemo(function () { return ncContextProps; }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        ncContextProps.DrawerComponent,
        ncContextProps.PageComponent,
        ncContextProps.isStandalonePage,
    ]);
    var renderPageContent = function () {
        return (React.createElement(React.Fragment, null,
            children,
            !disablePageWideNotifications && (React.createElement(Notifications, { wrapperClassName: notificationsWrapperClassName }, notificationsRenderFunc)),
            React.createElement(LiveNotifications, null)));
    };
    var renderSSRPopups = function () {
        var unverified = isLoggedIn && !publicSession.isVerified;
        var hasNotSeenVerificationPopup = firstTimeFlags[FirstTimeFlag.WelcomePopupNotClosed];
        if (unverified && hasNotSeenVerificationPopup) {
            return React.createElement(VerificationPopup, { dropFlagOnClose: true });
        }
    };
    var isSisu = useContext(SisuContext);
    // the error boundary here is really a last ditch effort - if the error
    // got here the entire page is dead already so we're just saving face
    return (React.createElement(ErrorBoundary, { debugComponent: "DaApp" },
        !disableHeadTags && (React.createElement(Head, { environment: environment, language: language, theme: theme, bodyClassName: bodyClassName, allowAppBanner: allowAppBanner, themeSkin: themeSkin })),
        !isDuperbrowseActive && appUrl && allowAppBanner && (React.createElement(DaAppUrl, { url: appUrl })),
        environment && ['dev', 'rc'].includes(environment) && React.createElement(FlagSwitcher, null),
        React.createElement(EditorBaseTheme, null),
        React.createElement(HoistCss, null),
        React.createElement(BiLoggerContextProvider, { value: biPageContextData },
            React.createElement(SessionContext.Provider, { value: publicSession },
                React.createElement(BrowserFeatureContext.Provider, { value: browserSupport },
                    React.createElement(MatureContext.Provider, { value: showMatureContent },
                        React.createElement(MobileContext.Provider, { value: isMobile },
                            React.createElement(HeadingLevelContext.Provider, { value: 1 },
                                React.createElement(UniqueIdContext.Provider, { value: uniqueIdSource },
                                    React.createElement(DndProvider, { backend: Backend },
                                        React.createElement(ThemeContext.Provider, { value: theme },
                                            React.createElement(ThemeSkinContext.Provider, { value: themeSkin },
                                                React.createElement(ScreenSizeContextProvider, null,
                                                    React.createElement(NCContext.Provider, { value: ncContextPropsMemo },
                                                        isDuperbrowseEnabled ? (React.createElement(DuperbrowsePageWrapper, { viewType: duperbrowseViewType }, renderPageContent())) : (renderPageContent()),
                                                        React.createElement(Modals, null, modalsRenderFunc),
                                                        !shouldHideBackToTop &&
                                                            backToTopOffset !== undefined && (React.createElement(BackToTop, { scrollToY: backToTopOffset })),
                                                        renderSSRPopups(),
                                                        showCookieBanner && !isIframe && !isSisu && (React.createElement(CookiesBanner, null)),
                                                        !disableChat && (React.createElement(ChatInit, { disableFloatingChat: disableFloatingChat })),
                                                        flags.includes('dre') && !isLegacyHeader && (React.createElement(DRE, { environment: environment })),
                                                        flags.includes('time_travel') &&
                                                            !isLegacyHeader && React.createElement(TimeTravel, null)))))))))))))));
};
DaApp.displayName = 'DaApp';
export default React.memo(DaApp, isEqual);
//# sourceMappingURL=DaApp.js.map